/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, Suspense, lazy, useEffect } from "react";
import { useIdleTimer } from "react-idle-timer";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import PrivateRoute from "./components/private-route/PrivateRoute";
import Preloader from "./components/preloader";
import { webRTCAdaptor } from "./components/dms-plugin/main";
import { callWSStartConnecting } from "./app/call/callSlice";
import { useAppDispatch, useAppSelector } from "./app/hooks";
import { chatWSConnecting, chatWSDisconnecting, setChatConfiguration } from "./app/chat/chatSlice";
import LeadsEmpty from "./components/chat/EmptyMessage";
import Empty from "./pages/empty";
import MessageAgentEmpty from "./pages/chat-agent/components/EmptyMessage";
import { WebRTCAdaptor } from "./antmedia/webrtc_adaptor";
import { useHttpRequest } from "./hooks/useHttpRequest";
import {
    dashboardWSConnecting,
    dashboardWSDisconnecting,
} from "./app/dashboard/dashboardSlice";

const ApplicationManagement = lazy(
    () => import("./pages/application-management/MainView")
);
const PartnerManagement = lazy(
    () => import("./pages/product-sponsor-management")
);
const UserRole = lazy(() => import("./pages/user-role"));
const AddUserRole = lazy(() => import("./pages/user-role/pages/AddUserRole"));
const EditUserRole = lazy(
    () => import("./pages/user-role/pages/EditUserRolePage")
);
const UserList = lazy(() => import("./pages/user-list"));
const EditUserList = lazy(() => import("./pages/user-list/pages/EditUserList"));
const ResetPassword = lazy(
    () => import("./pages/user-list/pages/ResetPassword")
);
const TeamManagement = lazy(
    () => import("./pages/user-list/pages/TeamManagement")
);

const Dashboard = lazy(() => import("./pages/dashboard"));
const Profile = lazy(() => import("./pages/profile/index"));
const ProfilePage = lazy(() => import("./pages/profile/components/profile"));
const ProfileEditPage = lazy(
    () => import("./pages/profile/components/profile-edit")
);
const NotificationPage = lazy(
    () => import("./pages/profile/components/notification")
);
const SettingsPage = lazy(() => import("./pages/profile/components/settings"));
const SecurityPage = lazy(() => import("./pages/profile/components/security"));
const ChatAgentPage = lazy(() => import("./pages/chat-agent/index"));
const MessageAgentContent = lazy(
    () => import("./pages/chat-agent/components/MessageBar")
);
const ChangePasswordPage = lazy(
    () => import("./pages/profile/components/security-changepassword")
);

const Signin = lazy(() => import("./pages/auth/index"));
const Signinbox = lazy(() => import("./pages/auth/SigninBox"));

const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ChangePassword = lazy(() => import("./pages/auth/ChangePassword"));
const OTP = lazy(() => import("./pages/auth/OTP"));
const OTPLogin = lazy(() => import("./pages/auth/OTPLogin"));
const SignOut = lazy(() => import("./pages/auth/SignOut"));
const Error500 = lazy(() => import("./pages/errors/error-500"));
const Error503 = lazy(() => import("./pages/errors/error-503"));
const Error505 = lazy(() => import("./pages/errors/error-505"));
const ErrorNotFound = lazy(() => import("./pages/errors/error-404"));
const ErrorForbidden = lazy(() => import("./pages/errors/error-403"));
const Leads = lazy(() => import("./pages/leads/index"));
const Recording = lazy(() => import("./pages/recording"));
const ExportRecording = lazy(() => import("./pages/export-recording"));
const ExportRecordingSales = lazy(
    () => import("./pages/export-recording/indexSales")
);
const AgentMonitoring = lazy(() => import("./pages/agent-monitoring"));
const CampaingMain = lazy(
    () => import("./pages/campaign-management/components/CampaignMain")
);
const AddCampaign = lazy(
    () => import("./pages/campaign-management/components/AddCampaign")
);
const SystemManagementEmpty = lazy(
    () => import("./pages/system-management/empty")
);

const LeadsContent = lazy(() => import("./pages/leads/components/LeadsBar"));

const ExtractApp = lazy(() => import("./pages/extract-application/index"));

const AgentProductionReport = lazy(
    () => import("./pages/report/agent_production-report")
);
const AgentProductionReportHierarchy = lazy(
    () => import("./pages/report/agent_production_hierarchy-report")
);
const DailyProductionReport = lazy(
    () => import("./pages/report/daily_production-report")
);
const CampaignProductivityReport = lazy(
    () => import("./pages/report/campaign_productivity-report")
);
const DCCalculationReport = lazy(
    () => import("./pages/report/dc_calculation_report")
);
const CallTrackingReport = lazy(
    () => import("./pages/report/call_tracking-report")
);
const CallMonitoringReport = lazy(
    () => import("./pages/report/call_monitoring-report")
);
const MonthlyQAReport = lazy(
    () => import("./pages/report/monthly_qa-report")
);
const CalculationDCByAgentReport = lazy(
    () => import("./pages/report/calculation_dc_by_agentdc")
);
const DailyQAReport = lazy(() => import("./pages/report/daily_qa_report"));
const QCRateReport = lazy(() => import("./pages/report/qc_rate-report"));
const MonthlyPerformanceReport = lazy(() => import("./pages/report/monthly_performance-report"));
import {
    IncomingChatAgentRoom,
    chatAgentWSConnecting,
} from "./app/chatAgent/chatAgentSlice";
import { setAvailable } from "./app/userState/userStateSlice";
import { logout } from "./app/auth/authSlice";
import { IResponse } from "./hooks/useHttp";
import { IIncomingRoom } from "./app/chatAgent/types";

const TemplateMain = lazy(
    () => import("./pages/upload-template/components/TemplateMain")
);
const UploadDataMain = lazy(
    () => import("./pages/upload-data/components/UploadDataMain")
);
const ExtractDataMain = lazy(
    () => import("./pages/extract-data/components/ExtractDataMain")
);
const ExtractTemplateMain = lazy(
    () => import("./pages/extract-data/components/ExtractTemplateMain")
);
const FormHeader = lazy(
    () => import("./pages/upload-template/components/FormHeader")
);
const UDFormHeader = lazy(
    () => import("./pages/upload-data/components/FormHeader")
);
const DataDetail = lazy(
    () => import("./pages/upload-data/components/DataDetail")
);
const BudgetMain = lazy(() => import("./pages/budget/index"));
const BlackListPDPMain = lazy(() => import("./pages/blacklist-pdp/index"));

const ScriptMain = lazy(
    () => import("./pages/product-script/components/ScriptMain")
);

const QaManagementIndexPage = lazy(
    () => import("./pages/qa-management/ListPage")
);
const QaManagementAddPage = lazy(() => import("./pages/qa-management/AddPage"));
const QaManagementEditPage = lazy(
    () => import("./pages/qa-management/EditPage")
);

const InteractionManagementIndexPage = lazy(
    () => import("./pages/interaction-management/ListPage")
);
const InteractionManagementAddPage = lazy(
    () => import("./pages/interaction-management/AddPage")
);
const InteractionManagementEditPage = lazy(
    () => import("./pages/interaction-management/EditPage")
);

const ApplicationMonitoringIndexPage = lazy(
    () => import("./pages/application-monitoring/ListPage")
);

const ApplicationMonitoringDetailPage = lazy(
    () => import("./pages/application-monitoring/DetailPage")
);

const CustomerUploadPurgingIndexPage = lazy(
    () => import("./pages/customer-upload-purging/ListPage")
);


const RecordingNonSalesQAScoreDetailPage = lazy(
    () => import("./pages/export-recording/DetailPage")
);

const PageManagementListPage = lazy(
    () => import("./pages/page-management/ListPage")
);

const PageManagementAddPage = lazy(
    () => import("./pages/page-management/FormPage")
);

const PageManagementEditPage = lazy(
    () => import("./pages/page-management/FormPage")
);

const MenuManagement = lazy(() => import("./pages/menu-management/ListPage"));

const MenuPageAccess = lazy(() => import("./pages/menu-page-access/ListPage"));

const MenuPageAccessAddPage = lazy(
    () => import("./pages/menu-page-access/FormPage")
);

const MenuPageAccessEditPage = lazy(
    () => import("./pages/menu-page-access/FormPage")
);

const ManualDial = lazy(
    () => import("./pages/manual-dialer/components/ManualDialerMain")
);

const TelcoProviderMain = lazy(() => import("./pages/telco-provider/index"));
const Approval = lazy(() => import("./pages/approval/index"));

const SchedulerMain = lazy(() => import("./pages/scheduler/index"));

const WhatsappBroadcastTemplateMain = lazy(() => import("./pages/whatsapp-broadcast-template"));
const WhatsappBroadcastTemplate = lazy(() => import("./pages/whatsapp-broadcast-template/components/BroadcastTemplate"));
const WhatsappBroadcast = lazy(() => import("./pages/whatsapp-broadcast"));
const WhatsappBroadcastConfiguration = lazy(() => import("./pages/whatsapp-broadcast-configuration"));
const ServiceGroup = lazy(() => import("./pages/service-group"));
const DetailServiceGroupView = lazy(() => import("./pages/service-group/components/DetailView"));
const ChatConfiguration = lazy(() => import("./pages/assignment-configuration"));
const AddBroadcastView = lazy(() => import('./pages/whatsapp-broadcast/components/AddBroadcastView'));

declare global {
    interface Window {
        getWindowLocation(): void;
        copyWindowLocation(): void;
        makeFullScreen(divId: any): void;
    }
}

export const AntmediaContext =
    React.createContext<WebRTCAdaptor>(webRTCAdaptor);
export const LayoutContext = React.createContext<any>(null);

const App: FC = () => {
    const chatAgentServiceUrl = process.env.REACT_APP_CHAT_AGENT_SERVICE;
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);
    const { allowedToLogout } = useAppSelector((state) => state.userstate);
    const { vCallStatus } = useAppSelector((state) => state.vcall);
    const { callState } = useAppSelector((state) => state.call);
    const leadsPath = "/leads";
    const systemManagementPath = "/system-management";
    const profilePath = "/profile";
    const chatPath = "/chat";
    const onIdle = () => {
        if (!callState.isCalling && allowedToLogout) {
            dispatch(chatWSDisconnecting());
            dispatch(dashboardWSDisconnecting());
            dispatch(setAvailable(false));
            dispatch(logout());
        }
    };
    useIdleTimer({
        onIdle,
        timeout: 1800000,
        throttle: 1000,
    });

    const { sendRequest } = useHttpRequest();
    const loadChatAgentRoom = async () => {
        const retrieveData = (res: IResponse | undefined) => {
            const data = res?.data;
            const chatRooms =
                data?.map((item) => {
                    return {
                        roomId: item.room_id,
                        sessionId: item.session_id,
                        fullName: item.room_name,
                        userName: item.username,
                        latestMessage: item.latest_message,
                        latestMessageTime: item.latest_message_time,
                        unreadMessageCount: item.unread_message_count,
                        status: item.status,
                        roleId: item.role_id,
                        participant: item.participant,
                    } as IIncomingRoom;
                }) ?? [];
            dispatch(IncomingChatAgentRoom(chatRooms));
        };
        const response = await sendRequest({
            baseUrl: chatAgentServiceUrl,
            url: `/Room/${user?.username}`,
            method: "get",
        });
        retrieveData(response);
    };
    useEffect(() => {
        if (user?.username)
            loadChatAgentRoom().catch((err) =>
                console.log("failed load chat agent room", err)
            );
    }, [user]);

    useEffect(() => {
        if (
            user?.deviceId != "" &&
            user?.deviceId !== undefined &&
            vCallStatus !== ""
        ) {
            dispatch(callWSStartConnecting());
            dispatch(chatAgentWSConnecting());
            dispatch(chatWSConnecting());
        }
    }, [user?.deviceId]);

    const loadChatConfig = async () => {
        const result = await sendRequest({
            baseUrl: String(process.env.REACT_APP_CHAT_SERVICE),
            url: `/configuration`,
            method: "get",
            params: { tenantid: user?.tenantId }
        });
        const data = result?.data as any;
        dispatch(setChatConfiguration({
            allowAttachment: data?.allow_attachment,
            maxAssignment: data?.max_assignment ?? 0,
            resolveOnOutboundSaveInteraction: data?.resolve_on_outbound_save_interaction ?? true
        }));
    };
    useEffect(() => {
        if (user?.tenantId)
            loadChatConfig().catch((err) => console.log("failed load chat configuration", err));
    }, [user?.tenantId]);

    return (
        <Router>
            <Suspense fallback={<Preloader />}>
                <AntmediaContext.Provider value={webRTCAdaptor}>

                    <Routes>
                        {/* Classic Routes */}

                        {/* Dashboard Routes */}
                        <Route
                            path="/"
                            element={
                                <PrivateRoute showSidebar={false}>
                                    <Empty />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/dashboard"
                            element={
                                <PrivateRoute showSidebar={false}>
                                    <Dashboard />
                                </PrivateRoute>
                            }
                        />
                        {/* Pages */}

                        <Route path="/signin" element={<Signin />}>
                            <Route index element={<Signinbox />} />
                            <Route
                                path="forgot-password"
                                element={<ForgotPassword />}
                            />
                            <Route path="otp" element={<OTP />} />
                            <Route path="otp-login" element={<OTPLogin />} />
                            <Route
                                path="change-password"
                                element={<ChangePassword />}
                            />
                        </Route>

                        <Route path="/signout" element={<SignOut />} />

                        {/* <Route
                            path={messagesPath}
                            element={
                                <PrivateRoute showSidebar={false}>
                                    <Messages />
                                </PrivateRoute>
                            }
                        >
                            <Route index element={<MessageEmpty />} />
                            <Route
                                path=":chatId"
                                element={<MessageContent />}
                            />
                        </Route> */}
                        <Route
                            path={leadsPath}
                            element={
                                <PrivateRoute showSidebar={false}>
                                    <Leads />
                                </PrivateRoute>
                            }
                        >
                            <Route index element={<LeadsEmpty />} />
                            <Route
                                path=":customerid"
                                element={<LeadsContent />}
                            />
                        </Route>
                        <Route
                            path={profilePath}
                            element={
                                <PrivateRoute showSidebar={false}>
                                    <Profile />
                                </PrivateRoute>
                            }
                        >
                            <Route index element={<ProfilePage />} />
                            <Route path="edit" element={<ProfileEditPage />} />
                            <Route
                                path="notification"
                                element={<NotificationPage />}
                            />
                            <Route path="settings" element={<SettingsPage />} />
                            <Route path="security" element={<SecurityPage />} />
                            <Route
                                path="change-password"
                                element={<ChangePasswordPage />}
                            />
                        </Route>
                        <Route path={systemManagementPath}>
                            <Route
                                index
                                element={
                                    <PrivateRoute>
                                        <SystemManagementEmpty />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="user-role"
                                element={
                                    <PrivateRoute>
                                        <UserRole />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="user-role/add"
                                element={
                                    <PrivateRoute>
                                        <AddUserRole />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="user-role/edit/:roleId"
                                element={
                                    <PrivateRoute>
                                        <EditUserRole />
                                    </PrivateRoute>
                                }
                            />

                            <Route
                                path="user-list"
                                element={
                                    <PrivateRoute>
                                        <UserList />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="user-list/:userid"
                                element={
                                    <PrivateRoute>
                                        <EditUserList />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="user-list/team-management"
                                element={
                                    <PrivateRoute>
                                        <TeamManagement />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="user-list/resetpassword/:userid"
                                element={
                                    <PrivateRoute>
                                        <ResetPassword />
                                    </PrivateRoute>
                                }
                            />

                            <Route
                                path="page-management"
                                element={
                                    <PrivateRoute>
                                        <PageManagementListPage />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="page-management/add"
                                element={
                                    <PrivateRoute>
                                        <PageManagementAddPage />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="page-management/:id"
                                element={
                                    <PrivateRoute>
                                        <PageManagementEditPage />
                                    </PrivateRoute>
                                }
                            />

                            <Route
                                path="menu-management/"
                                element={
                                    <PrivateRoute>
                                        <MenuManagement />
                                    </PrivateRoute>
                                }
                            />

                            <Route
                                path="menu-page-access/"
                                element={
                                    <PrivateRoute>
                                        <MenuPageAccess />
                                    </PrivateRoute>
                                }
                            />

                            <Route
                                path="menu-page-access/add"
                                element={
                                    <PrivateRoute>
                                        <MenuPageAccessAddPage />
                                    </PrivateRoute>
                                }
                            />

                            <Route
                                path="menu-page-access/:id"
                                element={
                                    <PrivateRoute>
                                        <MenuPageAccessEditPage />
                                    </PrivateRoute>
                                }
                            />

                            <Route
                                path="recording"
                                element={
                                    <PrivateRoute>
                                        <Recording />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="manual-dial"
                                element={
                                    <PrivateRoute>
                                        <ManualDial />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="campaign-management"
                                element={
                                    <PrivateRoute>
                                        <CampaingMain />
                                    </PrivateRoute>
                                }
                            >
                                <Route
                                    path=":campaignType"
                                    element={
                                        <PrivateRoute>
                                            <AddCampaign />
                                        </PrivateRoute>
                                    }
                                />
                            </Route>
                            <Route
                                path="agent-monitoring"
                                element={
                                    <PrivateRoute>
                                        <AgentMonitoring />
                                    </PrivateRoute>
                                }
                            />

                            <Route
                                path="export-recording"
                                element={
                                    <PrivateRoute>
                                        <ExportRecording />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="export-recording-nonsales"
                                element={
                                    <PrivateRoute>
                                        <ExportRecording />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="export-recording-nonsales/:id"
                                element={
                                    <PrivateRoute showSidebar={false}>
                                        <RecordingNonSalesQAScoreDetailPage />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="export-recording-sales"
                                element={
                                    <PrivateRoute>
                                        <ExportRecordingSales />
                                    </PrivateRoute>
                                }
                            />

                            <Route
                                path="product-sponsor-management"
                                element={
                                    <PrivateRoute>
                                        <PartnerManagement />
                                    </PrivateRoute>
                                }
                            >
                                {/* <Route path="add-sponsor-product" element={<PrivateRoute><AddSponsorMenu productsponsorid={null}/></PrivateRoute>}/> */}
                                {/* <Route path="ProductForm" element={<PrivateRoute><AddProduct productid={null}/></PrivateRoute>}/> */}
                                <Route
                                    path=":FormType"
                                    element={
                                        <PrivateRoute>
                                            <PartnerManagement />
                                        </PrivateRoute>
                                    }
                                />
                                {/* <Route path="SponsorForm" element={<PrivateRoute><AddSponsor sponsorid={null}/></PrivateRoute>}/> */}
                            </Route>

                            <Route
                                path="application-management"
                                element={
                                    <PrivateRoute>
                                        <ApplicationManagement />
                                    </PrivateRoute>
                                }
                            >
                                <Route
                                    path=":formType"
                                    element={
                                        <PrivateRoute>
                                            <ApplicationManagement />
                                        </PrivateRoute>
                                    }
                                />
                            </Route>

                            <Route
                                path="export-recording"
                                element={
                                    <PrivateRoute>
                                        <ExportRecording />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="upload-template"
                                element={
                                    <PrivateRoute>
                                        <TemplateMain />
                                    </PrivateRoute>
                                }
                            >
                                <Route
                                    path=":templateType"
                                    element={
                                        <PrivateRoute>
                                            <FormHeader />
                                        </PrivateRoute>
                                    }
                                ></Route>
                            </Route>
                            <Route
                                path="upload-data"
                                element={
                                    <PrivateRoute>
                                        <UploadDataMain />
                                    </PrivateRoute>
                                }
                            >
                                <Route
                                    path=":dataType"
                                    element={
                                        <PrivateRoute>
                                            <UDFormHeader />
                                        </PrivateRoute>
                                    }
                                ></Route>
                                <Route
                                    path=":dataType"
                                    element={
                                        <PrivateRoute>
                                            <DataDetail />
                                        </PrivateRoute>
                                    }
                                ></Route>
                            </Route>
                            <Route path="blacklist-pdp"
                                element={
                                    <PrivateRoute>
                                        <BlackListPDPMain />
                                    </PrivateRoute>
                                }>
                            </Route>
                            <Route
                                path="qa-management"
                                element={
                                    <PrivateRoute>
                                        <QaManagementIndexPage />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="qa-management/add"
                                element={
                                    <PrivateRoute>
                                        <QaManagementAddPage />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="qa-management/:id"
                                element={
                                    <PrivateRoute>
                                        <QaManagementEditPage />
                                    </PrivateRoute>
                                }
                            />

                            <Route
                                path="interaction-management"
                                element={
                                    <PrivateRoute>
                                        <InteractionManagementIndexPage />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="interaction-management/add"
                                element={
                                    <PrivateRoute>
                                        <InteractionManagementAddPage />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="interaction-management/:id"
                                element={
                                    <PrivateRoute>
                                        <InteractionManagementEditPage />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="product-script"
                                element={
                                    <PrivateRoute>
                                        <ScriptMain />
                                    </PrivateRoute>
                                }
                            >
                                <Route path=":scriptType"></Route>
                            </Route>

                            {/* Budget */}
                            <Route
                                path="budget"
                                element={
                                    <PrivateRoute>
                                        <BudgetMain />
                                    </PrivateRoute>
                                }
                            ></Route>
                            <Route
                                path="telco-provider"
                                element={
                                    <PrivateRoute>
                                        <TelcoProviderMain />
                                    </PrivateRoute>
                                }
                            ></Route>
                            <Route
                                path="approval"
                                element={
                                    <PrivateRoute>
                                        <Approval />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="extract-data"
                                element={
                                    <PrivateRoute>
                                        <ExtractDataMain />
                                    </PrivateRoute>
                                }
                            >
                                <Route path=":formType"></Route>
                            </Route>
                            <Route
                                path="extract-template"
                                element={
                                    <PrivateRoute>
                                        <ExtractTemplateMain />
                                    </PrivateRoute>
                                }
                            >
                                <Route path=":formType"></Route>
                            </Route>
                            <Route
                                path="agent_production-reports"
                                element={
                                    <PrivateRoute>
                                        <AgentProductionReport />
                                    </PrivateRoute>
                                }
                            ></Route>
                            <Route
                                path="extracted-application"
                                element={
                                    <PrivateRoute>
                                        <ExtractApp />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="agent_production-reports"
                                element={
                                    <PrivateRoute>
                                        <AgentProductionReport />
                                    </PrivateRoute>
                                }
                            ></Route>
                            <Route
                                path="apr_hierarchy-reports"
                                element={
                                    <PrivateRoute>
                                        <AgentProductionReportHierarchy />
                                    </PrivateRoute>
                                }
                            ></Route>
                            <Route
                                path="daily_production-reports"
                                element={
                                    <PrivateRoute>
                                        <DailyProductionReport />
                                    </PrivateRoute>
                                }
                            ></Route>
                            <Route
                                path="campaign_productivity-reports"
                                element={
                                    <PrivateRoute>
                                        <CampaignProductivityReport />
                                    </PrivateRoute>
                                }
                            ></Route>
                            <Route
                                path="daily_qa-reports"
                                element={
                                    <PrivateRoute>
                                        <DailyQAReport />
                                    </PrivateRoute>
                                }
                            ></Route>
                            <Route
                                path="qc_rate-reports"
                                element={
                                    <PrivateRoute>
                                        <QCRateReport />
                                    </PrivateRoute>
                                }
                            ></Route>
                            <Route
                                path="dc_calculation-reports"
                                element={
                                    <PrivateRoute>
                                        <DCCalculationReport />
                                    </PrivateRoute>
                                }
                            ></Route>
                            <Route
                                path="call_tracking-reports"
                                element={
                                    <PrivateRoute>
                                        <CallTrackingReport />
                                    </PrivateRoute>
                                }
                            ></Route>
                            <Route
                                path="call_monitoring-reports"
                                element={
                                    <PrivateRoute>
                                        <CallMonitoringReport />
                                    </PrivateRoute>
                                }
                            ></Route>
                            <Route
                                path="monthly_qa-reports"
                                element={
                                    <PrivateRoute>
                                        <MonthlyQAReport />
                                    </PrivateRoute>
                                }
                            ></Route>
                            <Route
                                path="calculation_dc_agent-reports"
                                element={
                                    <PrivateRoute>
                                        <CalculationDCByAgentReport />
                                    </PrivateRoute>
                                }
                            ></Route>
                            <Route
                                path="monthly_performance-reports"
                                element={
                                    <PrivateRoute>
                                        <MonthlyPerformanceReport />
                                    </PrivateRoute>
                                }
                            ></Route>
                            <Route
                                path="application"
                                element={
                                    <PrivateRoute>
                                        <ApplicationMonitoringIndexPage />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="application/:id"
                                element={
                                    <PrivateRoute showSidebar={false}>
                                        <ApplicationMonitoringDetailPage />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="customer-upload-purging"
                                element={
                                    <PrivateRoute>
                                        <CustomerUploadPurgingIndexPage />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="scheduler"
                                element={
                                    <PrivateRoute>
                                        {/* <UserRole /> */}
                                        <SchedulerMain />
                                    </PrivateRoute>
                                }
                            >
                                <Route path=":schedulerType"></Route>
                            </Route>

                            <Route
                                path="whatsapp-broadcast-template"
                                element={
                                    <PrivateRoute>
                                        <WhatsappBroadcastTemplateMain />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path="whatsapp-broadcast-template/:templateId"
                                element={
                                    <PrivateRoute>
                                        <WhatsappBroadcastTemplate />
                                    </PrivateRoute>
                                }
                            />
                            <Route path="whatsapp-broadcast-config"
                                element={<PrivateRoute><WhatsappBroadcastConfiguration /></PrivateRoute>}
                            />
                            <Route path="service-group"
                                element={<PrivateRoute><ServiceGroup /></PrivateRoute>}
                            />
                            <Route path="service-group/:serviceGroupId"
                                element={<PrivateRoute><DetailServiceGroupView /></PrivateRoute>}
                            />
                            <Route path="chat-configuration"
                                element={<PrivateRoute><ChatConfiguration /></PrivateRoute>}
                            />

                            <Route path="whatsapp-broadcast-outbound"
                                element={<PrivateRoute><WhatsappBroadcast /></PrivateRoute>}
                            />

                            <Route path="whatsapp-broadcast-outbound/:WABroadcastId"
                                element={<PrivateRoute><AddBroadcastView /></PrivateRoute>}
                            />
                        </Route>

                        <Route
                            path={chatPath}
                            element={
                                <PrivateRoute showSidebar={false}>
                                    <ChatAgentPage layoutType="PAGE" />
                                </PrivateRoute>
                            }
                        >
                            <Route index element={<MessageAgentEmpty />} />
                            <Route
                                path=":chatId"
                                element={<MessageAgentContent />}
                            />
                        </Route>

                        {/* Error Pages */}
                        <Route path="/error-403" element={<ErrorForbidden />} />
                        <Route path="/error-500" element={<Error500 />} />
                        <Route path="/error-503" element={<Error503 />} />
                        <Route path="/error-505" element={<Error505 />} />

                        {/* 404 Page Route */}
                        <Route path="*" element={<ErrorNotFound />} />
                    </Routes>
                </AntmediaContext.Provider>
            </Suspense>
        </Router>
    );
};

export default App;
